import React from 'react';
import type { Dispatch, SetStateAction, RefObject } from 'react';

import Konva from 'konva';

import {
  faFont,
  faImage,
  faShapes,
  faPalette,
  faLayerGroup,
} from '@fortawesome/pro-solid-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

// tabs
import TabCard from 'Components/CanvasEditor/components/tabs/TabCard';
import Text from 'Components/CanvasEditor/components/tabs/Text';
import Images from 'Components/CanvasEditor/components/tabs/Images';
import Shapes from 'Components/CanvasEditor/components/tabs/Shapes';
import Background from 'Components/CanvasEditor/components/tabs/Background';
import Layers from 'Components/CanvasEditor/components/tabs/Layers';

import type { CanvasBackground, CanvasElement, SidebarTab } from 'Components/CanvasEditor/types';

const tabs: SidebarTab[] = [
  { id: 'text', title: 'Text', icon: faFont },
  { id: 'images', title: 'Photos', icon: faImage },
  {
    id: 'shapes',
    title: 'Shapes',
    icon: faShapes,
  },
  {
    id: 'background',
    title: 'Background',
    icon: faPalette,
  },
  {
    id: 'layers',
    title: 'Layers',
    icon: faLayerGroup,
  },
];

interface SidebarPanelProps {
  stageRef: RefObject<Konva.Stage>;
  layerRef: RefObject<Konva.Layer>;
  activeTab: string;
  onTabChange: Dispatch<SetStateAction<string>>;
  onSelectElement: (id: string | null) => void;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
  onAddElement: (element: CanvasElement) => void;
  background: CanvasBackground;
  onBackgroundChange: (background: CanvasBackground) => void;
  selectedId: string | null;
  elements: CanvasElement[];
  setNewElements: Dispatch<SetStateAction<CanvasElement[]>>;
  onDeleteElement: (id: string) => void;
}

export default function SidebarPanel({
  stageRef,
  layerRef,
  activeTab,
  onTabChange,
  onSelectElement,
  onElementChange,
  onAddElement,
  onBackgroundChange,
  background,
  selectedId,
  elements,
  setNewElements,
  onDeleteElement,
}: SidebarPanelProps) {
  return (
    <FlexBox flexDirection="column" UNSAFE_className="canvas-elements-sidebar">
      <FlexBox alignItems="center" UNSAFE_className="canvas-elements-sidebar-tabs">
        {tabs.map(tab => (
          <TabCard
            key={tab.id}
            tab={tab}
            isActiveTab={tab.id === activeTab}
            onTabChange={onTabChange}
          />
        ))}
      </FlexBox>
      <FlexBox UNSAFE_className="canvas-elements-sidebar-content">
        {activeTab === 'text' && <Text stageRef={stageRef} onAddElement={onAddElement} />}
        {activeTab === 'images' && <Images stageRef={stageRef} onAddElement={onAddElement} />}
        {activeTab === 'shapes' && <Shapes stageRef={stageRef} onAddElement={onAddElement} />}
        {activeTab === 'background' && (
          <Background onBackgroundChange={onBackgroundChange} background={background} />
        )}
        {activeTab === 'layers' && (
          <Layers
            layerRef={layerRef}
            onSelectElement={onSelectElement}
            onElementChange={onElementChange}
            selectedId={selectedId}
            elements={elements}
            setNewElements={setNewElements}
            onDeleteElement={onDeleteElement}
          />
        )}
      </FlexBox>
    </FlexBox>
  );
}
